import { useEffect, useMemo, useState } from "react";
import FrequencyDistribution from "../../../components/graphs/FrequencyDistribution";
import InverterMpptProfile from "../../../components/graphs/InverterMPPTProfile";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartColumn, faGear } from "@fortawesome/free-solid-svg-icons";
import { apiFetch } from "../../../api/apiClient";
import DayRangePicker from "../DayRangePicker";

function toLocalIsoDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export default function SolarPerformancePanel({
  portfolioName = "",
  range = null,
  onRangeChange,
  onTooltip,
  powerblocks = [],
  selectedPowerblockId = "",
  onPowerblockChange,
  data = {},
}) {
  const [loadedData, setLoadedData] = useState({
    inverterMpptProfile: [],
    frequencyDistribution: [],
    metrics: {},
  });
  const startDate = toLocalIsoDate(range?.start);
  const endDate = toLocalIsoDate(range?.end);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!portfolioName || !selectedPowerblockId || !startDate || !endDate) {
        setLoadedData({
          inverterMpptProfile: [],
          frequencyDistribution: [],
          metrics: [],
        });
        return;
      }

      const query = new URLSearchParams({
        device: "powerblock",
        powerblockId: String(selectedPowerblockId),
        start: startDate,
        end: endDate,
      }).toString();

      const mpptUrl = `/api/graph/portfolio/${portfolioName}/insights/range/mppt?${query}`;
      const frequencyUrl = `/api/graph/portfolio/${portfolioName}/insights/range/frequency-distribution?${query}`;

      try {
        const [inverterMpptProfile, frequencyDistribution] = await Promise.all([
          apiFetch(mpptUrl),
          apiFetch(frequencyUrl),
        ]);

        if (cancelled) return;
        setLoadedData({
          inverterMpptProfile: inverterMpptProfile.results,
          frequencyDistribution: frequencyDistribution.results,
          metrics:
            inverterMpptProfile.metrics && frequencyDistribution.metrics
              ? [ ...inverterMpptProfile.metrics, ...frequencyDistribution.metrics ]
              : inverterMpptProfile.metrics || frequencyDistribution.metrics || {}
        });
      } catch (error) {
        console.warn("Solar performance panel API fetch failed:", error);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [portfolioName, startDate, endDate, selectedPowerblockId]);

  const panelData = useMemo(() => {
    return {
      ...data,
      inverterMpptProfile: loadedData.inverterMpptProfile,
      frequencyDistribution: loadedData.frequencyDistribution,
      metrics: loadedData.metrics,
    };
  }, [data, loadedData]);

  return (
    <div id="solarperformance" className="panel p-3 mb-5">
      <div className="header d-flex justify-content-between pb-3 mb-3">
        <div className="left d-flex gap-3">
          <span className="d-flex align-items-center gap-2 flex-shrink-0"><FontAwesomeIcon icon={faChartColumn} /> SOLAR INVERTER PERFORMANCE</span>
          <DayRangePicker
            range={range}
            setRange={onRangeChange}
            maxRangeDays={null}
            lookbackMonths={6}
          />
          <select
            className="form-select"
            value={selectedPowerblockId}
            onChange={(e) => onPowerblockChange?.(e.target.value)}
            aria-label="Powerblock"
            disabled={!powerblocks.length}
          >
            {powerblocks.map(({ id, name }) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </div>
        <div className="right d-flex align-items-stretch gap-2">
          <button type="button"><FontAwesomeIcon icon={faGear} /></button>
        </div>
      </div>
      <div className="d-flex align-items-center">
        <div className="col-4">
          <InverterMpptProfile
            portfolioName={portfolioName}
            duration="range"
            startDate={startDate}
            endDate={endDate}
            powerblockId={selectedPowerblockId}
            data={panelData.inverterMpptProfile}
            onTooltip={onTooltip}
          />
        </div>
        <div className="col-4">
          <FrequencyDistribution
            portfolioName={portfolioName}
            duration="range"
            startDate={startDate}
            endDate={endDate}
            powerblockId={selectedPowerblockId}
            data={panelData.frequencyDistribution}
            onTooltip={onTooltip}
          />
        </div>
        <div className="col ms-5">
          <div className="solarPerformanceMetrics">
            {Array.isArray(panelData.metrics) && panelData.metrics.length > 0 ? (
              panelData.metrics.map((metric, idx) => {
                if (!metric || !('label' in metric) || !('value' in metric) || metric.label == null || metric.value == null) {
                  return null;
                }
                return (
                  <div className="d-flex flex-row align-items-center gap-2 justify-content-between" key={metric.label || idx}>
                    <strong>{metric.label}</strong> {metric.value}
                    {metric.unit ? <>&nbsp;{metric.unit}</> : null}
                  </div>
                );
              })
            ) : (
              <div><em>No metrics available.</em></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
